// src/hooks/useNotifications.js - in-app notifications + FCM token registration
import { useState, useEffect, useRef } from "react";
import {
  collection, onSnapshot, query, where,
  limit, updateDoc, doc, addDoc, setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getToken, onMessage, deleteToken } from "firebase/messaging";
import { auth, db, functions, getMessagingInstance } from "../firebase/config";

const PERMISSION_SEEN_KEY = "dghelpmate_notification_permission_seen";
const PERMISSION_LAST_ASKED_KEY = "dghelpmate_notification_permission_last_asked";
const PERMISSION_SESSION_ASKED_KEY = "dghelpmate_notification_permission_session_asked";
const CURRENT_TOKEN_KEY = "dghelpmate_current_fcm_token_hash";
const DEFAULT_ICON = "/images/logo.png";
const PERMISSION_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function canUseNotifications() {
  return typeof window !== "undefined" && "Notification" in window && window.isSecureContext;
}

function cleanUserAgent() {
  if (typeof navigator === "undefined") return "";
  return String(navigator.userAgent || "").replace(/\s+/g, " ").slice(0, 180);
}

function detectDeviceMeta() {
  const ua = cleanUserAgent();
  const lower = ua.toLowerCase();
  const isTablet = /ipad|tablet|playbook|silk/.test(lower) || (/android/.test(lower) && !/mobile/.test(lower));
  const isMobile = !isTablet && /mobile|android|iphone|ipod|windows phone/.test(lower);

  let browser = "unknown";
  if (/edg\//i.test(ua)) browser = "edge";
  else if (/opr\//i.test(ua) || /opera/i.test(ua)) browser = "opera";
  else if (/firefox\//i.test(ua)) browser = "firefox";
  else if (/chrome\//i.test(ua) || /crios\//i.test(ua)) browser = "chrome";
  else if (/safari\//i.test(ua)) browser = "safari";

  let os = "unknown";
  if (/windows/i.test(ua)) os = "windows";
  else if (/android/i.test(ua)) os = "android";
  else if (/iphone|ipad|ipod/i.test(ua)) os = "ios";
  else if (/mac os/i.test(ua)) os = "macos";
  else if (/linux/i.test(ua)) os = "linux";

  return {
    platform: isMobile || isTablet ? "mobile" : "desktop",
    browser,
    os,
    deviceType: isTablet ? "tablet" : isMobile ? "mobile" : ua ? "desktop" : "unknown",
    userAgent: ua,
  };
}

async function sha256(input) {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const data = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, "0")).join("");
  }

  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return `fallback_${Math.abs(hash)}`;
}

async function getReadyServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return existing;
  return navigator.serviceWorker.register("/sw.js");
}

// Write a notification document for the in-app bell. The backend trigger sends push.
export async function sendNotification({ toUserId, toRole, title, body, type, link, priority = "normal", relatedId = "" }) {
  if (!toUserId) return;
  try {
    await addDoc(collection(db, "notifications"), {
      toUserId,
      toRole: toRole || "client",
      title,
      body,
      type: type || "info",
      link: link || "",
      priority,
      relatedId,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch(e) {
    console.error("Notification error:", e);
  }
}

export async function sendAdminNotification({
  title,
  body,
  type = "info",
  link = "",
  relatedId = "",
  priority = "normal",
  adminUserId = "",
  fromUserId = "",
  fromName = "",
} = {}) {
  const currentUser = auth.currentUser;
  const senderId = fromUserId || currentUser?.uid || "";
  if (!senderId || !title || !body) return;
  try {
    await addDoc(collection(db, "notifications"), {
      toUserId: adminUserId || "admin",
      toRole: "admin",
      title: String(title).trim(),
      body: String(body).trim(),
      type,
      link: link || "",
      fromUserId: senderId,
      fromName: fromName || currentUser?.displayName || currentUser?.email || "User",
      priority,
      relatedId,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch(e) {
    console.error("Admin notification error:", e);
  }
}

export async function requestNotificationPermission() {
  if (!canUseNotifications()) return "unsupported";
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }
  try {
    localStorage.setItem(PERMISSION_SEEN_KEY, "true");
    localStorage.setItem(PERMISSION_LAST_ASKED_KEY, String(Date.now()));
    sessionStorage.setItem(PERMISSION_SESSION_ASKED_KEY, "true");
    return await Notification.requestPermission();
  } catch (e) {
    console.error("Notification permission error:", e);
    return Notification.permission || "default";
  }
}

export function shouldPromptForNotificationPermission({ force = false } = {}) {
  if (!canUseNotifications()) return false;
  if (Notification.permission === "granted" || Notification.permission === "denied") return false;
  if (force) return true;

  try {
    if (sessionStorage.getItem(PERMISSION_SESSION_ASKED_KEY) === "true") return false;
    const lastAsked = Number(localStorage.getItem(PERMISSION_LAST_ASKED_KEY) || 0);
    if (lastAsked && Date.now() - lastAsked < PERMISSION_PROMPT_COOLDOWN_MS) return false;
  } catch {
    return true;
  }

  return Notification.permission === "default";
}

export async function ensureNotificationPermissionForPush(userId = auth.currentUser?.uid, opts = {}) {
  if (!canUseNotifications()) return { permission: "unsupported", token: null, prompted: false };

  let permission = Notification.permission;
  let prompted = false;

  if (permission === "default" && shouldPromptForNotificationPermission({ force: opts.force === true })) {
    prompted = true;
    permission = await requestNotificationPermission();
  }

  const token = permission === "granted" && userId
    ? await registerFcmToken(userId)
    : null;

  return { permission, token, prompted };
}

export async function registerFcmToken(userId = auth.currentUser?.uid) {
  if (!userId || !canUseNotifications() || Notification.permission !== "granted") return null;
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey || vapidKey.startsWith("REPLACE_")) {
    console.warn("FCM VAPID key not set; push tokens disabled");
    return null;
  }

  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    const swReg = await getReadyServiceWorker();
    if (!swReg) return null;

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return null;

    const tokenHash = await sha256(token);
    const meta = detectDeviceMeta();
    const tokenRef = doc(db, "fcmTokens", userId, "tokens", tokenHash);

    await setDoc(tokenRef, {
      userId,
      token,
      tokenHash,
      ...meta,
      permission: "granted",
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    }, { merge: true });

    localStorage.setItem(CURRENT_TOKEN_KEY, tokenHash);
    return token;
  } catch(e) {
    console.error("FCM token registration error:", e);
    return null;
  }
}

// Backward-compatible export used by login/portal pages.
export const registerFCMToken = registerFcmToken;

export async function unregisterCurrentFcmToken(userId = auth.currentUser?.uid) {
  if (!userId) return false;
  try {
    const messaging = await getMessagingInstance();
    const tokenHash = localStorage.getItem(CURRENT_TOKEN_KEY);
    if (tokenHash) {
      await setDoc(doc(db, "fcmTokens", userId, "tokens", tokenHash), {
        active: false,
        permission: canUseNotifications() ? Notification.permission : "unsupported",
        disabledAt: serverTimestamp(),
        disabledReason: "client_unregister",
        updatedAt: serverTimestamp(),
      }, { merge: true });
      localStorage.removeItem(CURRENT_TOKEN_KEY);
    }
    if (messaging) await deleteToken(messaging).catch(() => false);
    return true;
  } catch (e) {
    console.error("FCM token unregister error:", e);
    return false;
  }
}

export async function sendTestNotification({ title = "DG HelpMate Test", body = "Browser push is working.", link = "/" } = {}) {
  try {
    const call = httpsCallable(functions, "sendTestPushNotification");
    return await call({ title, body, link });
  } catch (e) {
    console.error("Test push error:", e);
    return { data: { success: false, error: e.message } };
  }
}

function showBrowserNotif(title, body, data = {}) {
  if (!canUseNotifications() || Notification.permission !== "granted") return;
  try {
    const url = data.link || data.url || "/";
    const n = new Notification(title || "DG HelpMate", {
      body: body || "",
      icon: data.icon || DEFAULT_ICON,
      badge: data.badge || DEFAULT_ICON,
      image: data.image || undefined,
      tag: data.notificationId ? `dghelpmate-${data.notificationId}` : `dghelpmate-${Date.now()}`,
      data: { ...data, url },
      requireInteraction: data.priority === "high" || data.type === "urgent" || data.type === "order",
    });
    n.onclick = () => {
      if (url) window.open(url, "_blank");
      n.close();
    };
    setTimeout(() => n.close(), data.type === "urgent" ? 12000 : 6000);
  } catch(e) {
    console.error("Browser notif error:", e);
  }
}

export default function useNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState(
    canUseNotifications() ? Notification.permission : "unsupported"
  );
  const [fcmReady, setFcmReady] = useState(false);
  const shownIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (canUseNotifications()) setPermission(Notification.permission);
  }, []);

  useEffect(() => {
    if (!userId || !canUseNotifications() || Notification.permission !== "granted") return;
    registerFcmToken(userId).then(token => setFcmReady(!!token));
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const q = query(
      collection(db, "notifications"),
      where("toUserId", "==", userId),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      const notifs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
      setLoading(false);

      if (!isFirstLoad.current) {
        notifs.forEach(n => {
          if (!n.read && !shownIds.current.has(n.id)) {
            shownIds.current.add(n.id);
            showBrowserNotif(n.title, n.body, {
              notificationId: n.id,
              type: n.type,
              link: n.link || "/",
              priority: n.priority,
            });
          }
        });
      } else {
        notifs.forEach(n => shownIds.current.add(n.id));
        isFirstLoad.current = false;
      }
    });
    return unsub;
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    let unsubFcm;
    getMessagingInstance().then((messaging) => {
      if (!messaging) return;
      unsubFcm = onMessage(messaging, (payload) => {
        const data = payload.data || {};
        const notificationId = data.notificationId || data.notifId || "";
        if (notificationId && shownIds.current.has(notificationId)) return;
        if (notificationId) shownIds.current.add(notificationId);

        const title = payload.notification?.title || data.title;
        const body = payload.notification?.body || data.body;
        if (title) {
          showBrowserNotif(title, body, {
            ...data,
            notificationId,
            link: data.link || data.url || "/",
          });
        }
      });
    });
    return () => unsubFcm?.();
  }, [userId]);

  const markRead = async (id) => {
    try {
      await updateDoc(doc(db, "notifications", id), { read: true });
    } catch(e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    await Promise.all(unread.map(n =>
      updateDoc(doc(db, "notifications", n.id), { read: true }).catch(() => {})
    ));
  };

  const requestPermission = async () => {
    const perm = await requestNotificationPermission();
    setPermission(perm);
    if (perm === "granted" && userId) {
      const token = await registerFcmToken(userId);
      setFcmReady(!!token);
      return !!token;
    }
    return perm === "granted";
  };

  const subscribeToPush = async () => {
    if (permission !== "granted") return requestPermission();
    const token = await registerFcmToken(userId);
    setFcmReady(!!token);
    return !!token;
  };

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    requestPermission,
    permission,
    permissionPromptSeen: typeof localStorage !== "undefined" && localStorage.getItem(PERMISSION_SEEN_KEY) === "true",
    fcmReady,
    isSubscribed: fcmReady,
    subscribeToPush,
    unregisterCurrentFcmToken: () => unregisterCurrentFcmToken(userId),
    sendTestNotification,
  };
}
